export enum UserRole {
  USER = 'user', // Personal app user
  CLIENT = 'client', // Served by a business
  STAFF = 'staff', // Works under a business
  BUSINESS_ADMIN = 'business_admin', // Manages a business account
  MODERATOR = 'moderator', // Manages content or flags
  SUPPORT = 'support', // Handles user support and tickets
  PLATFORM_ADMIN = 'platform_admin', // Full platform access
}

export enum SubscriptionPlan {
  FREE = 'free', // Free Tier – Explore Your Palette
  PLUS = 'plus', // Plus – Unlimited analyses, save & export, history
  FREEMIUM_TRIAL = 'freemium_trial', // Freemium Trial – Try the Full Experience (7 Days Free)
  PRO = 'pro', // Pro Plan – Built for Creators, Stylists & Beauty Pros
  PAY_PER_SCAN = 'pay_per_scan', // Pay-per-scan – One-time deep color analysis
}

export enum Permission {
  // View permissions
  VIEW_HOME = 'view:home',
  VIEW_OUTFITS = 'view:outfits',
  VIEW_ANALYSIS = 'view:analysis',
  VIEW_MOODBOARDS = 'view:moodboards',
  VIEW_PROFILE = 'view:profile',
  VIEW_OTHER_PROFILE = 'view:other_profile',

  // Create permissions
  CREATE_ANALYSIS = 'create:analysis',
  CREATE_OUTFIT = 'create:outfit',
  CREATE_MOODBOARD = 'create:moodboard',

  // Edit permissions
  EDIT_PROFILE = 'edit:profile',
  EDIT_MOODBOARD = 'edit:moodboard',

  // Delete permissions
  DELETE_MOODBOARD = 'delete:moodboard',

  // Action permissions
  LIKE_MOODBOARD = 'like:moodboard',
  SAVE_OUTFIT = 'save:outfit',

  // Color Analysis permissions
  BASIC_COLOR_ANALYSIS = 'feature:basic_color_analysis', // 5 results + 3 to avoid
  FULL_COLOR_ANALYSIS = 'feature:full_color_analysis', // Unlimited results + saving
  SAVE_COLOR_RESULTS = 'feature:save_color_results', // Save and export results
  SHARE_COLOR_RESULTS = 'feature:share_color_results', // Share results

  // Drape Test permissions
  DAILY_DRAPE_TEST = 'feature:daily_drape_test', // 1 per day (primary + secondary)
  UNLIMITED_DRAPE_TEST = 'feature:unlimited_drape_test', // Full library access
  SEASONAL_PALETTE_ACCESS = 'feature:seasonal_palette_access', // Seasonal palettes

  // Recommendations permissions
  BASIC_RECOMMENDATIONS = 'feature:basic_recommendations', // Basic makeup, hair, nail
  PREMIUM_RECOMMENDATIONS = 'feature:premium_recommendations', // Enhanced recommendations

  // Smart Wardrobe permissions
  LIMITED_SMART_WARDROBE = 'feature:limited_smart_wardrobe', // Up to 3 items
  UNLIMITED_SMART_WARDROBE = 'feature:unlimited_smart_wardrobe', // Unlimited items
  WARDROBE_EXPORT = 'feature:wardrobe_export', // Export wardrobe
  AI_OUTFIT_SUGGESTIONS = 'feature:ai_outfit_suggestions', // AI outfit suggestions

  // Fit Check permissions
  LIMITED_FIT_CHECK = 'feature:limited_fit_check', // Limited with ads
  UNLIMITED_FIT_CHECK = 'feature:unlimited_fit_check', // No ads, unlimited

  // Community permissions
  LIMITED_COMMUNITY_ACCESS = 'feature:limited_community_access', // Limited moodboard access
  FULL_COMMUNITY_ACCESS = 'feature:full_community_access', // Full community access
  LIMITED_COMMUNITY_QUESTIONS = 'feature:limited_community_questions', // 2 questions per day
  UNLIMITED_COMMUNITY_QUESTIONS = 'feature:unlimited_community_questions', // No limits
  PRIORITY_COMMUNITY_SUPPORT = 'feature:priority_community_support', // Priority support

  // Moodboard permissions
  LIMITED_MOODBOARDS = 'feature:limited_moodboards', // Basic moodboards
  UNLIMITED_MOODBOARDS = 'feature:unlimited_moodboards', // Unlimited moodboards
  PROFESSIONAL_MOODBOARD_TEMPLATES = 'feature:professional_moodboard_templates', // Premium templates

  // Pro features
  AI_COLOR_CAPSULES = 'feature:ai_color_capsules', // AI-generated Color Capsules
  EARLY_ACCESS_FEATURES = 'feature:early_access_features', // Experimental features
  INSIDER_STYLING_TIPS = 'feature:insider_styling_tips', // Styling tips

  // Pay-per-scan features
  DEEP_COLOR_ANALYSIS = 'feature:deep_color_analysis', // One-time deep analysis
}

export type RolePermissions = {
  [key in UserRole]: Permission[];
};

export interface User {
  id: string;
  email: string;
  name?: string;
  role: UserRole;
  subscription_plan: SubscriptionPlan;
  profileImageUrl?: string;
  bio?: string;
  onboarding_completed?: boolean;
  is_admin?: boolean;
  color_generator_used_at?: string;
  virtual_drape_used_at?: string;
  color_analysis_used_at?: string;
  email_verified?: boolean;
}
